import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../config/apiConfig.js';
import fetchApi from '../../utils/api.js';
import '../../styles/AddPaymentModal.css';
import AirDatePicker from '../ui/AirDatePicker.js';

const AddPaymentModal = ({ isOpen, onClose, entityName, entityType, entityId, paymentToEdit }) => {
    // autofill current date and time
    const getCurrentDate = () => {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const getCurrentTime = () => {
        const now = new Date();
        return now.toTimeString().slice(0, 5);
    };

    const [formData, setFormData] = useState({
        paymentMode: '',
        paymentDate: '',
        paymentTime: '',
        amount: '',
        kasarAmount: '',
        paymentType: 'Credit',
        voucherType: '',
        bank: '',
        checkNumber: '',
        checkDate: '',
        remark: '',
        document: null
    });

    const [paymentModes, setPaymentModes] = useState([]);
    const [voucherTypes, setVoucherTypes] = useState([]);
    const [banks, setBanks] = useState([]);
    const [fileName, setFileName] = useState('No file chosen');
    const [balance, setBalance] = useState(null);

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const [modes, vouchers, bankList] = await Promise.all([
                    fetchApi('/payment-mode?limit=100'),
                    fetchApi('/voucher-type?limit=100'),
                    fetchApi('/bank?limit=100')
                ]);
                setPaymentModes(Array.isArray(modes) ? modes : (modes.paymentModes || []));
                setVoucherTypes(vouchers.voucherTypes || vouchers || []);
                setBanks(bankList.banks || bankList || []);

                if (modes.length > 0) {
                    setFormData(prev => ({ ...prev, paymentMode: modes[0].paymentModeName }));
                }
            } catch (error) {
                console.error("Error fetching dropdown data:", error);
            }
        };
        if (isOpen) {
            fetchDropdownData();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            if (paymentToEdit) {
                let dateStr = paymentToEdit.paymentDate || paymentToEdit.date || getCurrentDate();
                if (dateStr.includes('/')) {
                    const parts = dateStr.split('/');
                    if (parts.length === 3) dateStr = `${parts[2]}-${parts[1]}-${parts[0]}`;
                }

                setFormData({
                    paymentMode: paymentToEdit.mode || paymentToEdit.paymentModeName || '',
                    paymentDate: dateStr,
                    paymentTime: paymentToEdit.time || getCurrentTime(),
                    amount: paymentToEdit.amount || paymentToEdit.paymentAmount || '',
                    kasarAmount: paymentToEdit.kasarAmount || paymentToEdit.kasar_amount || '',
                    paymentType: paymentToEdit.paymentType || 'Credit',
                    voucherType: paymentToEdit.voucherType || '',
                    bank: paymentToEdit.bank || '',
                    checkNumber: paymentToEdit.checkNumber || '',
                    checkDate: paymentToEdit.checkDate || '',
                    remark: paymentToEdit.narration || paymentToEdit.remark || '',
                    document: null
                });
            } else {
                setFormData(prev => ({
                    ...prev,
                    paymentMode: paymentModes.length > 0 ? paymentModes[0].paymentModeName : '',
                    paymentDate: getCurrentDate(),
                    paymentTime: getCurrentTime(),
                    amount: '',
                    kasarAmount: '',
                    paymentType: 'Credit',
                    voucherType: '',
                    bank: '',
                    checkNumber: '',
                    checkDate: '',
                    remark: '',
                    document: null
                }));
            }
            setFileName('No file chosen');
        }
    }, [isOpen, paymentModes, paymentToEdit]);

    useEffect(() => {
        const fetchBalance = async () => {
            try {
                let baseRoute = '';
                if (entityType === 'Customer') baseRoute = '/invoice-payment';
                else if (entityType === 'Seller') baseRoute = '/purchase-order-payment';
                else if (entityType === 'Transporter') baseRoute = '/transporter-payment';

                if (baseRoute && entityId) {
                    const totalsData = await fetchApi(`${baseRoute}/totals/${entityId}`);
                    const bal = totalsData.balance !== undefined ? totalsData.balance : totalsData.pendingAmount;
                    setBalance(bal);
                }
            } catch (error) {
                console.error("Error fetching entity balance:", error);
            }
        };

        if (isOpen && entityId) {
            fetchBalance();
        } else {
            setBalance(null);
        }
    }, [isOpen, entityId, entityType]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            if (name === 'paymentType') {
                newData.voucherType = ''; // Reset voucher when payment type changes
            }
            if (name === 'voucherType') {
                if (value && value.toLowerCase().includes('cash')) {
                    newData.bank = '';
                    newData.checkNumber = '';
                    newData.checkDate = '';
                }
            }
            return newData;
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, document: file }));
            setFileName(file.name);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.amount) {
            toast.error("Please enter payment amount");
            return;
        }

        try {
            let endpoint = '';
            let payload = {
                paymentAmount: formData.amount,
                kasarAmount: formData.kasarAmount || 0,
                paymentDate: formData.paymentDate,
                paymentTime: formData.paymentTime,
                paymentModeName: formData.paymentMode,
                paymentType: formData.paymentType,
                voucherType: formData.voucherType,
                bank: formData.bank,
                checkNumber: formData.checkNumber,
                checkDate: formData.checkDate,
                remark: formData.remark,
            };

            if (entityType === 'Customer') {
                endpoint = '/invoice-payment';
                payload.customerId = entityId;
                payload.customerName = entityName;
            } else if (entityType === 'Seller') {
                endpoint = '/purchase-order-payment';
                payload.buyerId = entityId;
                payload.buyerTradeName = entityName;
            } else if (entityType === 'Transporter') {
                endpoint = '/transporter-payment';
                payload.transporterId = entityId;
                payload.transporterName = entityName;
            }

            if (paymentToEdit) {
                endpoint += `/${paymentToEdit._id || paymentToEdit.id}`;
                await fetchApi(endpoint, {
                    method: 'PUT',
                    body: JSON.stringify(payload)
                });
                toast.success(`${entityType} payment updated successfully!`);
            } else {
                await fetchApi(endpoint, {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
                toast.success(`${entityType} payment recorded successfully!`);
            }

            onClose();
        } catch (error) {
            console.error("Payment Submission Error:", error);
            toast.error(error.message || "Failed to record payment");
        }
    };

    if (!isOpen) return null;

    const isCash = formData.voucherType && formData.voucherType.toLowerCase().includes('cash');
    const isBank = formData.voucherType && formData.voucherType.toLowerCase().includes('bank');

    return (
        <div className="payment-modal-overlay">
            <div className="payment-modal-content">
                {balance !== null && (
                    <div className="payment-modal-balance">
                        Balance: ₹{parseFloat(balance).toFixed(2)}
                    </div>
                )}
                <button className="payment-modal-close" onClick={onClose}>&times;</button>
                <div className="payment-modal-header" style={{ textAlign: 'center', marginBottom: '15px' }}>
                    <h2 className="payment-modal-title" style={{ display: 'inline-block', borderBottom: '2px solid #0b3a54', paddingBottom: '4px' }}>
                        {paymentToEdit ? 'Edit Payment' : 'Add Payment'}
                    </h2>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Row 1: Date, Time */}
                    <div className="payment-row">
                        <div className="payment-form-group">
                            <label className="payment-label">Payment Date</label>
                            <AirDatePicker
                                value={formData.paymentDate}
                                onChange={(dateStr) => setFormData(prev => ({ ...prev, paymentDate: dateStr }))}
                                className="payment-input"
                            />
                        </div>
                        <div className="payment-form-group">
                            <label className="payment-label">Payment Time</label>
                            <input
                                type="time"
                                name="paymentTime"
                                className="payment-input"
                                value={formData.paymentTime}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* Row 2: Amount, Kasar Amount, Type */}
                    <div className="payment-row-three">
                        <div className="payment-form-group">
                            <label className="payment-label">Payment Amount</label>
                            <input
                                type="number"
                                name="amount"
                                placeholder="Payment Amount"
                                className="payment-input"
                                value={formData.amount}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="payment-form-group">
                            <label className="payment-label">Kasar Amount</label>
                            <input
                                type="number"
                                name="kasarAmount"
                                placeholder="Kasar Amount"
                                className="payment-input"
                                value={formData.kasarAmount}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="payment-form-group">
                            <label className="payment-label">Payment Type</label>
                            <select
                                name="paymentType"
                                className="payment-select"
                                value={formData.paymentType}
                                onChange={handleChange}
                            >
                                <option value="Credit">Credit</option>
                                <option value="Debit">Debit</option>
                            </select>
                        </div>
                    </div>

                    {/* Row 3: Voucher Type, Bank */}
                    <div className="payment-row">
                        <div className="payment-form-group">
                            <label className="payment-label">Voucher Type</label>
                            <select
                                name="voucherType"
                                className="payment-select"
                                value={formData.voucherType}
                                onChange={handleChange}
                            >
                                <option value="">Select Voucher</option>
                                {voucherTypes
                                    .filter(v => {
                                        const name = (v.voucherTypeName || '').toLowerCase();
                                        if (formData.paymentType === 'Credit') {
                                            return name === 'bank receipt' || name === 'cash receipt';
                                        } else {
                                            return name === 'bank payment' || name === 'cash payment';
                                        }
                                    })
                                    .map(v => (
                                        <option key={v._id} value={v.voucherTypeName}>
                                            {v.voucherTypeName}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div className="payment-form-group">
                            <label className="payment-label">Bank {isBank && <span style={{color: 'red'}}>*</span>}</label>
                            <select
                                name="bank"
                                className="payment-select"
                                value={formData.bank}
                                onChange={handleChange}
                                disabled={isCash}
                                required={isBank}
                            >
                                <option value="">Select Bank</option>
                                {banks.map(b => (
                                    <option key={b._id} value={b.bankName}>
                                        {b.bankName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Row 4: Check Number, Check Date */}
                    <div className="payment-row">
                        <div className="payment-form-group">
                            <label className="payment-label">Check Number</label>
                            <input
                                type="text"
                                name="checkNumber"
                                placeholder="Payment Check Number"
                                className="payment-input"
                                value={formData.checkNumber}
                                onChange={handleChange}
                                disabled={isCash}
                            />
                        </div>
                        <div className="payment-form-group">
                            <label className="payment-label">Check Date</label>
                            <AirDatePicker
                                value={formData.checkDate}
                                onChange={(dateStr) => setFormData(prev => ({ ...prev, checkDate: dateStr }))}
                                className="payment-input"
                                disabled={isCash}
                            />
                        </div>
                    </div>

                    {/* Row 5: Remark */}
                    <div className="payment-form-group full-width">
                        <label className="payment-label">Remark</label>
                        <textarea
                            name="remark"
                            placeholder="Enter Remark"
                            className="payment-textarea"
                            value={formData.remark}
                            onChange={handleChange}
                        ></textarea>
                    </div>

                    <div className="payment-modal-actions" style={{ marginTop: '20px' }}>
                        <button type="submit" className="payment-btn-submit" style={{ backgroundColor: '#0b3a54', color: 'white', padding: '10px 30px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>Submit</button>
                        <button type="button" className="payment-btn-cancel" onClick={onClose} style={{ backgroundColor: '#eef2f7', color: '#64748b', padding: '10px 30px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddPaymentModal;
